import { useEffect, useState } from "react";
import { api } from "../../api/client";
import type { AthleteDetail } from "../../api/types";
import { BodyPanel } from "../../components/body";
import { ConversationPanel } from "../../components/ConversationPanel";
import { ProfileForm } from "../../components/ProfileForm";
import { ProfileSummary } from "../../components/ProfileSummary";
import { Button, ErrorState, Loading, PageHead, useToast } from "../../components/ui";
import { paceShort, rangeText } from "../../lib/format";
import { useAsync } from "../../lib/useAsync";
import { t } from "../../i18n";

function useMyAthlete(orgId: string) {
  return useAsync(() => api.get<AthleteDetail>(`/orgs/${orgId}/me/athlete`), [orgId]);
}

export function AthleteProfilePage({ orgId }: { orgId: string }) {
  const toast = useToast();
  const state = useMyAthlete(orgId);
  const [athlete, setAthlete] = useState<AthleteDetail | null>(null);
  const [editing, setEditing] = useState(false);
  useEffect(() => setAthlete(state.data), [state.data]);

  if (state.loading && !athlete) return <Loading />;
  if (state.error) return <ErrorState error={state.error} onRetry={state.reload} />;
  if (!athlete) return null;

  return (
    <>
      <PageHead
        title={t("Meu perfil")}
        subtitle={t("Suas preferências e marcas ajudam o treinador a montar a planilha.")}
        actions={!editing && <Button variant="primary" onClick={() => setEditing(true)}>{t("Editar perfil")}</Button>}
      />
      <section className="panel">
        <div className="panel-body">
          {editing ? (
            <ProfileForm
              mode="self"
              profile={athlete.profile}
              onCancel={() => setEditing(false)}
              onSubmit={async (payload) => {
                setAthlete(await api.patch<AthleteDetail>(`/orgs/${orgId}/me/athlete`, { profile: payload }));
                setEditing(false);
                toast(t("Perfil salvo."));
              }}
            />
          ) : (
            <ProfileSummary profile={athlete.profile} showCoachFields={false} />
          )}
        </div>
      </section>
      {!editing && athlete.profile && (athlete.profile.training_zones.length > 0 || athlete.profile.sport_notes) && (
        <section className="panel section">
          <div className="panel-head">
            <h2>{t("Definido pelo treinador")}</h2>
          </div>
          <div className="panel-body">
            <ProfileSummaryCoachPart athlete={athlete} />
          </div>
        </section>
      )}
    </>
  );
}

function ProfileSummaryCoachPart({ athlete }: { athlete: AthleteDetail }) {
  const p = athlete.profile!;
  return (
    <div className="stack">
      {p.sport_notes && <p style={{ whiteSpace: "pre-wrap" }}>{p.sport_notes}</p>}
      {p.training_zones.length > 0 && (
        <div className="table-wrap">
          <table className="data">
            <thead>
              <tr>
                <th>{t("Zona")}</th>
                <th className="right">{t("FC (bpm)")}</th>
                <th className="right">{t("Ritmo")}</th>
              </tr>
            </thead>
            <tbody>
              {p.training_zones.map((z, i) => (
                <tr key={i}>
                  <td>{z.name}</td>
                  <td className="right num">{rangeText(z.hr_min_bpm, z.hr_max_bpm)}</td>
                  <td className="right num">{rangeText(z.pace_min_s_km, z.pace_max_s_km, paceShort)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

export function AthleteMessagesPage({ orgId }: { orgId: string }) {
  return (
    <>
      <PageHead title={t("Mensagens")} subtitle={t("Converse com o seu treinador e a equipe da assessoria.")} />
      <section className="panel" aria-label={t("Conversa com a equipe")}>
        <div className="panel-body">
          <ConversationPanel orgId={orgId} self empty={t("Nenhuma mensagem ainda. Escreva abaixo para falar com a equipe.")} />
        </div>
      </section>
    </>
  );
}

export function AthleteBodyPage({ orgId }: { orgId: string }) {
  return (
    <>
      <PageHead title={t("Peso e IMC")} subtitle={t("Registre o seu peso toda semana ou todo mês. Só você vê, a menos que autorize a equipe.")} />
      <BodyPanel orgId={orgId} />
    </>
  );
}
